import { useMainDict } from "@/libs/hooks/useTranslation";
import { MainCarousel } from "./Carousel";
import Link from "next/link";
import { useSettings } from "@/libs/hooks/useSettings";
import useGAnalytics, {
  EventAction,
  EventCategory,
  EventLabel,
} from "@/libs/hooks/tracking/useGAnalytics";
import { useCopies } from "@/libs/hooks/useCopy";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";

export const WelcomeSectionComponent = () => {
  const { t } = useMainDict();
  const { settings } = useSettings();
  const { copy } = useCopies();

  const { trackEvent } = useGAnalytics();

  const handleOurStoryLinkClick = () => {
    trackEvent(
      "our-story-link-clicked",
      EventCategory.LINKS,
      EventAction.CLICK,
      EventLabel.OUR_STORY
    );
  };

  return (
    <section className="relative w-full overflow-hidden">
      {/* Vintage burgundy background with texture */}
      <div className="absolute inset-0 bg-gradient-to-br from-vintage-burgundy via-[#6B2D35] to-[#5A252C]">
        {/* Subtle pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        {/* Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.3)_100%)]" />
      </div>

      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-ochre/50 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto flex lg:flex-row flex-col">
        {/* Carousel with vintage frame */}
        <div className="relative lg:w-1/2">
          <MainCarousel />
        </div>

        {/* Content area */}
        <div className="flex-1 lg:justify-start justify-center">
          {copy?.welcomeCopy && (
            <div className="flex-1 lg:max-w-xl lg:min-h-full min-h-72 h-full flex flex-col justify-center p-8 lg:p-12">
              {/* Decorative element */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-px bg-gradient-to-r from-vintage-ochre to-transparent" />
                <span className="text-vintage-ochre text-xl">✦</span>
              </div>

              {/* Content with vintage typography */}
              <div className="text-vintage-cream font-serif text-xl md:text-2xl leading-relaxed space-y-4 vintage-drop-cap">
                <BlocksRenderer
                  content={copy.welcomeCopy}
                  blocks={{
                    paragraph: ({ children }) => (
                      <p className="mb-4 leading-relaxed">{children}</p>
                    ),
                  }}
                />
              </div>

              {/* Our Story button with vintage styling */}
              {settings?.showOurStoryLink && (
                <div className="mt-8">
                  <Link
                    href="/our-story"
                    onClick={handleOurStoryLinkClick}
                    className="group inline-flex items-center gap-3 vintage-btn vintage-btn-gold"
                  >
                    <span>{t("readOurStory")}</span>
                    <svg
                      className="w-4 h-4 transition-transform group-hover:translate-x-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </Link>
                </div>
              )}

              {/* Decorative element */}
              <div className="flex items-center gap-4 mt-8">
                <span className="text-vintage-ochre text-xl">✦</span>
                <div className="w-16 h-px bg-gradient-to-l from-vintage-ochre to-transparent" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Decorative bottom border */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-ochre/50 to-transparent" />
    </section>
  );
};
